
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

export const useSession = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let mounted = true;
    let previousUserId: string | null = null;

    const initializeSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        if (mounted) {
          // If user changes, invalidate cache
          const currentUserId = session?.user?.id || null;
          if (previousUserId && previousUserId !== currentUserId) {
            console.log("User changed from session initialization, resetting cache");
            queryClient.clear();
          }
          
          previousUserId = currentUserId;
          setUser(session?.user || null);
        }
      } catch (error: any) {
        console.error('Error initializing session:', error);
        toast.error('Erro ao inicializar sessão');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        const currentUserId = session?.user?.id || null;
        
        // If auth state changed and the user ID changed, reset cache
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || previousUserId !== currentUserId) {
          console.log('Auth state changed:', event);
          
          // Reset cache when user changes
          if (previousUserId !== currentUserId) {
            console.log("User changed on auth state change, resetting cache");
            queryClient.clear();
          }
          
          previousUserId = currentUserId;
        }
        
        setUser(session?.user || null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  return { user, loading };
};
